import { Play, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { routeBuilders } from '../routes';
import { slugify } from '../../utils/slug';
import { useState } from 'react';

interface StoryCardProps {
  story: {
    id: string;
    title: string;
    author: string;
    creatorSlug?: string | null;
    creatorName?: string | null;
    coverImage: string;
    duration: string;
    totalChapters: number;
    genreIds: string[];
    rating?: number;
    listens?: number;
  };
  onPlay: (story: any) => void;
  onLike?: (storyId: string) => void;
  isLiked?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export function StoryCard({ story, onPlay, onLike, isLiked = false, size = 'medium' }: StoryCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [liked, setLiked] = useState(isLiked);

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLiked(!liked);
    onLike?.(story.id);
  };

  const creatorSlug = story.creatorSlug ?? slugify(story.author);
  const creatorPath = creatorSlug ? routeBuilders.creatorPublic(creatorSlug) : null;

  const sizeClasses = {
    small: 'w-32',
    medium: 'w-40',
    large: 'w-full'
  };

  return (
    <div 
      className={`${size === 'large' ? 'w-full' : sizeClasses[size]} flex-shrink-0 cursor-pointer group`}
      onClick={() => onPlay(story)}
    >
      <div className="relative aspect-square rounded-2xl overflow-hidden bg-base-200 mb-3 shadow-sm">
        <img
          src={story.coverImage}
          alt={story.title}
          className={`w-full h-full object-cover transition-all duration-300 ${
            imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          } group-hover:scale-105`}
          onLoad={() => setImageLoaded(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral/60 via-neutral/0 to-neutral/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <button
          className="btn btn-primary btn-square btn-sm absolute bottom-3 right-3 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0"
          onClick={(e) => {
            e.stopPropagation();
            onPlay(story);
          }}
        >
          <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
        </button>
        {onLike && (
          <button
            className="btn btn-ghost btn-primary btn-square btn-xs absolute top-3 right-3 bg-neutral/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300"
            onClick={handleLike}
          >
            <Heart
              className={`w-4 h-4 transition-colors ${liked ? 'fill-current' : ''}`}
            />
          </button>
        )}
      </div>
      <div className="space-y-1">
        <h3 className="font-semibold line-clamp-2 leading-tight">{story.title}</h3>
        {creatorPath ? (
          <Link
            to={creatorPath}
            onClick={(e) => e.stopPropagation()}
            className="text-sm text-base-content/70 hover:text-base-content transition-colors"
          >
            {story.author}
          </Link>
        ) : (
          <p className="text-sm text-base-content/60">{story.author}</p>
        )}
        <p className="text-xs text-base-content/50">{story.duration} · {story.totalChapters} chapters</p>
      </div>
    </div>
  );
}
